import "./style.scss";

import { Card } from "@amzn/stencil-react-components/card";
import { Col, Row, Spacer } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import { Text } from "@amzn/stencil-react-components/text";
import IconCloseCircleFill from "@amzn/stencil-react-icons/icons/icon-close-circle-fill";
import IconThumbDown from "@amzn/stencil-react-icons/icons/icon-thumb-down";
import IconThumbDownFill from "@amzn/stencil-react-icons/icons/icon-thumb-down-fill";
import IconThumbUp from "@amzn/stencil-react-icons/icons/icon-thumb-up";
import IconThumbUpFill from "@amzn/stencil-react-icons/icons/icon-thumb-up-fill";
import { trackUxmCustomEvent } from "common/helpers/analytics-helper";
import { getString } from "common/uistringlabels/uiStringUtils";
import { UtilHelper } from "lib/helpers";
import React, { FunctionComponent, useState } from "react";

/**
 * Allowed names for the CFM module, used to get the config file for the module.
 * For the feature name sent to Quitanieves review each config file.
 */
export enum CFMModuleName {
  orderFlow = "orderFlow", // Feature name: OrderSoftware.
  amazonEssentials = "amazonEssentials",
  exploreItmp = "exploreItmp",
  hardwareOrderConfirmation = "hardwareOrderConfirmation",
  mostPopular = "mostPopular",
  productDetails = "productDetails",
  hardwareProductDetails = "hardwareProductDetails",
  searchResults = "searchResults",
  searchExperience = "searchExperience",
  popularInTeam = "popularInTeam",
  hardwareSearchResults = "hardwareSearchResults",
  myOrdersSoftware = "myOrdersSoftware",
  myOrdersHardware = "myOrdersHardware",
}

export interface CustomerFeedbackProps {
  /**
   * Module name to load the questions and the title to load
   */
  moduleName: CFMModuleName;

  /**
   * The text alignment for the text
   */
  alignment: CustomerFeedbackAlignment;

  /**
   * The thumbs up icon position
   */
  position: CustomerFeedbackIconPosition;

  /**
   * Pop up X position
   */
  popUpXAlignment?: CustomerFeedbackPopUpAlignment;

  /**
   * Pop up Y position
   */
  popUpYAlignment?: CustomerFeedbackPopUpHeight;

  /**
   * Text size for CFM question
   */
  textSize?: string;

  /**
   * Width of the row that contains CFM
   */
  width?: string;

  /**
   * Sets a margin on the component
   */
  margin?: string;
}

export enum CustomerFeedbackAlignment {
  Left = 1,
  Centered,
  Right,
}

export enum CustomerFeedbackIconPosition {
  Left = "left",
  Bottom = "bottom",
}

export enum CustomerFeedbackPopUpHeight {
  Left = "left",
  Bottom = "none",
  Top = "top",
}

export enum CustomerFeedbackPopUpAlignment {
  Left,
  Right,
}

export enum CustomerFeedbackMargins {
  Top = "top",
  None = "none",
}

export const CustomerFeedback: FunctionComponent<CustomerFeedbackProps> = ({
  moduleName,
  alignment,
  position,
  popUpXAlignment,
  popUpYAlignment,
  textSize,
  width,
  margin = CustomerFeedbackMargins.Top,
}) => {
  const [upvoted, setUpvoted] = useState(false);
  const [downvoted, setDownvoted] = useState(false);
  const [cfmActive, setCfmActive] = useState(false);

  // istanbul ignore next
  const resetCfmWidget = (event: any) => {
    event.preventDefault();
    const widget = document.getElementById(`cfm-widget-${moduleName}`);
    if (cfmActive) {
      // @ts-ignore
      widget?.destroy();
      setUpvoted(false);
      setDownvoted(false);
      setCfmActive(false);
    }

    return false;
  };

  // istanbul ignore next
  const onUpvoteClick = (event: any) => {
    const widget = document.getElementById(`cfm-widget-${moduleName}`);

    // TODO: Update component to use Stencil Feedback module
    const parent = widget?.parentElement?.parentElement?.parentElement;
    if (parent) {
      parent.style.zIndex = "99999";
    }

    event.preventDefault();
    const wasUpvoted = !upvoted;
    resetCfmWidget(event);
    setUpvoted(wasUpvoted);
    if (wasUpvoted) {
      const configSettings = UtilHelper.getCustomerFeedbackConfiguration(`${moduleName}`);
      // initialize CFM widget and start
      // @ts-ignore
      widget?.initialize(configSettings);
      // start CFM script
      // @ts-ignore
      widget?.start();
      // Preselect the highest rating
      // WARNING: This is not oficially supported, calling private method to set the rating to 5
      // @ts-ignore
      widget?._ratingClickHandler(5);

      trackUxmCustomEvent(`cfm-${moduleName}`, { rating: 5 });

      setCfmActive(true);
    }

    return false;
  };

  // istanbul ignore next
  const onDownvoteClick = (event: any) => {
    const widget = document.getElementById(`cfm-widget-${moduleName}`);

    // TODO: Update component to use Stencil Feedback module
    const parent = widget?.parentElement?.parentElement?.parentElement;
    if (parent) {
      parent.style.zIndex = "99999";
    }
    event.preventDefault();
    const wasDownvoted = !downvoted;
    resetCfmWidget(event);
    setDownvoted(wasDownvoted);
    if (wasDownvoted) {
      const configSettings = UtilHelper.getCustomerFeedbackConfiguration(`${moduleName}`);
      // initialize CFM widget and start
      // @ts-ignore
      widget?.initialize(configSettings);
      // start CFM script
      // @ts-ignore
      widget?.start();
      // Preselect the lowest rating
      // WARNING: This is not oficially supported, calling private method to set the rating to 0
      // @ts-ignore
      widget?._ratingClickHandler(0);

      trackUxmCustomEvent(`cfm-${moduleName}`, { rating: 0 });

      setCfmActive(true);
    }

    return false;
  };

  // istanbul ignore next
  const getCardDisplay = (): string => {
    if (upvoted || downvoted) {
      return "flex";
    }
    return "none";
  };

  // instanbul ignore next
  const getIconPositioning = (): React.CSSProperties => {
    if (position == CustomerFeedbackIconPosition.Bottom) {
      return {
        alignItems: "center",
        flexDirection: "column",
      };
    }
    return {};
  };

  // instanbul ignore next
  const getPopUpCardHeight = (): string => {
    const popUpPosition: string = popUpYAlignment ?? position;
    switch (popUpPosition) {
      case CustomerFeedbackPopUpHeight.Bottom:
        return "50px";
      case CustomerFeedbackPopUpHeight.Top:
        return "-340px";
      default:
        return "30px";
    }
  };

  // instanbul ignore next
  const getRowTopMargin = (): string => {
    return margin == CustomerFeedbackMargins.Top ? "S400" : "0";
  };

  // instanbul ignore next
  const getPopUpLeftPosition = (): string => {
    return popUpXAlignment === CustomerFeedbackPopUpAlignment.Right ? "-100px" : "-411px";
  };

  return (
    <>
      <Row width={width ?? "100%"} margin={[getRowTopMargin(), 0, 0, 0]}>
        {(alignment === CustomerFeedbackAlignment.Right || alignment == CustomerFeedbackAlignment.Centered) && (
          <Spacer flex="1"></Spacer>
        )}
        <div className="cfm-main" style={getIconPositioning()}>
          <div>
            {/* Text */}
            <Text
              className="cfm-text"
              fontSize={textSize ?? "T100"}
              fontWeight="regular"
              color="neutral70"
              margin={[0, "S200", 0, 0]}
              data-cy={`cfm-title-${moduleName}`}
            >
              {getString(`customerFeedback.${moduleName}`)}
            </Text>
          </div>
          <div>
            {/* Icons */}
            <Link
              href="#"
              margin={[0, "S200", 0, 0]}
              onClick={onUpvoteClick}
              className="thumbsup-icon"
              data-cy={`${moduleName}-thumbsup`}
            >
              {!upvoted && <IconThumbUp title={getString("customerFeedback.thumbsUpTitle")} color="neutral90" />}
              {upvoted && <IconThumbUpFill title={getString("customerFeedback.thumbsUpTitle")} color="primary80" />}
            </Link>
            <Link href="#" onClick={onDownvoteClick} className="thumbsup-icon" data-cy={`${moduleName}-thumbsdown`}>
              {!downvoted && <IconThumbDown title={getString("customerFeedback.thumbsUpTitle")} color="neutral90" />}
              {downvoted && <IconThumbDownFill title={getString("customerFeedback.thumbsUpTitle")} color="primary80" />}
            </Link>
          </div>
        </div>
        {/* Pop Up Card */}
        <div style={{ display: getCardDisplay(), position: "relative" }} className="cfm">
          <Card
            isElevated={true}
            style={{ position: "absolute", left: getPopUpLeftPosition(), top: getPopUpCardHeight() }}
          >
            <Col>
              <Row>
                <Spacer flex="1"></Spacer>
                <Link href="#" onClick={resetCfmWidget}>
                  <IconCloseCircleFill title={getString("customerFeedback.closeCFM")} color="primary80" />
                </Link>
              </Row>
              <Row>
                <git-feedback-inline-widget
                  id={`cfm-widget-${moduleName}`}
                  data-cy={`cfm-widget-${moduleName}`}
                ></git-feedback-inline-widget>
              </Row>
            </Col>
          </Card>
        </div>
        {alignment === CustomerFeedbackAlignment.Centered && <Spacer flex="1"></Spacer>}
      </Row>
    </>
  );
};
